import React, { useEffect, useState, useRef, useCallback, useMemo } from "react";
import { Box, Grid2, Typography } from "@mui/material";
import CartLoader from "../../../Helper/Loaders/CartLoader";
import { getDiagnosticsAnalyticsTimeSeries } from "../../../Api/BmsFaultsApi";
import { calculateGranularity } from "../CommonFunctions";
import { formatDateLabel, formatDateForAPI, parseDateRangeFromLabel } from "../../../Helper/Analytics/AnalyticsHelper";
import ChartDrillDownModalV2 from "./ChartDrillDown/ChartDrillDownModalV2";
import { CHART_CONFIG, CHART_TYPES } from "./chartConfig";
import RenderChartLegend from "../../AdvancedAnalytics/ContinuousChart/RenderChartLegend";
import ScrollableChartWrapper from "./ScrollableColumnChart/ScrollableChartWrapper";
import { calculateNiceScale, generateYLabelsWithPositions } from "./ScrollableColumnChart/ChartScaleUtils";
import { useChartDrillDown } from "./hooks/useChartDrillDown";
import { formatDateRangeWithSmartYear } from "../../../Helper/DatePicker/DateFormatters";
import { useBaseEntityInfo } from "../../../Hooks/usePagesContent";
import { useBmsMetaInfoFields, useBmsTableConfig } from "./useBmsTableConfig";

const AutoResolvedChartView = ({ isSidebarExpanded, startDate, endDate, granularity, filters = [], hierarchicalLocation, fleetIds, onboardingLocations, allOnboardingLocations, alertCategories, operationalStatus, searchTerm, inDetailsView = false }) => {
  const { baseEntityType, baseEntityTypeIDLable, baseEntityTypeLable, baseEntityTypePluralLable } = useBaseEntityInfo();
  const [isResizing, setIsResizing] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [loading, setLoading] = useState(true);
  const [chartData, setChartData] = useState({
    categories: [],
    dateRanges: [], // Store original date ranges from API
    autoResolvedData: [],
    manuallyResolvedData: []
  });

  // Use ref to track previous values and prevent duplicate API calls
  const prevDepsRef = useRef('');

  // Configure drill-down hook with chart-specific overrides
  // Columns come from the bmsAlertV2 org meta when it configures them, and from
  // chartConfig otherwise, so an unconfigured org is unaffected.
  const resolveTable = useBmsTableConfig();
  const resolveMetaInfo = useBmsMetaInfoFields();

  const drillDownConfig = useMemo(() => ({
    chartType: CHART_TYPES.AUTO_RESOLVED,
    csvFileNamePrefix: "BmsFaults_FaultResolution",

    getLevel1Filters: (dataPoint, baseFilters) => {
      const resolutionType = dataPoint.metricName === "Auto Resolved" ? "AutoResolved" : "ManualResolved";
      return [...baseFilters, { name: "ResolutionType", values: [resolutionType] }];
    },

    getLevel1Select: () => ["ResolvedFaultCount", "avgResolutionDuration", "minResolutionDuration", "maxResolutionDuration"],
    getLevel1MetaInfo: () => resolveMetaInfo([], "autoResolvedSummaryTable"),

    getTableConfig: (level, dataPoint) => {
      const autoResolvedConfig = CHART_CONFIG[CHART_TYPES.AUTO_RESOLVED];
      if (level === 1) {
        const summaryTable = autoResolvedConfig?.popupConfig?.summaryTable;
        if (!summaryTable) return null;

        // Update column name dynamically based on selected metric
        const tableColumnsConfig = { ...summaryTable.tableColumnsConfig };
        const ResolvedFaultCount = tableColumnsConfig?.ResolvedFaultCount;
        if (ResolvedFaultCount && dataPoint) {
          tableColumnsConfig.ResolvedFaultCount = {
            ...ResolvedFaultCount,
            title: `Fault ${dataPoint?.metricName || "Resolved"}`,
          };
        }

        // Org meta replaces the columns wholesale, including the metric rename
        // above, which only applies to the chartConfig defaults.
        const resolved = resolveTable("autoResolvedSummaryTable", {
          ...summaryTable,
          tableColumnsConfig,
        });
        return {
          title: summaryTable.title,
          tableColumnsConfig: resolved.tableColumnsConfig,
          tableColumnOrder: resolved.tableColumnOrder,
          emptyMessage: "No alerts found for the selected criteria",
        };
      }

      if (level === 2) {
        const vehiclesTable = autoResolvedConfig?.popupConfig?.vehiclesTable;
        if (!vehiclesTable) return null;

        const resolved = resolveTable("autoResolvedVehiclesTable", vehiclesTable);
        return {
          title: vehiclesTable.title,
          tableColumnsConfig: resolved.tableColumnsConfig,
          tableColumnOrder: resolved.tableColumnOrder,
          emptyMessage: `No ${baseEntityTypePluralLable.toLowerCase()} found for the selected fault`,
        };
      }

      return null;
    },

    // Level 1: Custom modal title
    getModalTitle: (dataPoint) => {
      const resolutionType = dataPoint?.metricName || "Faults";
      const category = formatDateRangeWithSmartYear(dataPoint?.startDate || startDate, dataPoint?.endDate || endDate);
      return (
        <Grid2 container gap={1} alignItems={"center"}>
          <Typography variant="fw500s16c8F8F8F" sx={{ lineHeight: "28px" }}>
            Fault Resolution Trend (Auto vs Manual)
            <Typography variant="fw500s16c000000" sx={{ lineHeight: "28px" }}>
              {' / '}{resolutionType}
            </Typography>
          </Typography>
          <Typography variant="fw500s14c686868" sx={{ lineHeight: "20px" }}>
            {'•'}
          </Typography>
          <Typography variant="fw500s14c686868" sx={{ lineHeight: "20px" }}>
            {category}
          </Typography>
        </Grid2>
      );
    },

    // Level 1: Custom CSV filename
    getCSVFileName: (dataPoint) => {
      const resolutionType = dataPoint?.metricName || "Resolved";
      const formattedStartDate = new Date(dataPoint?.startDate || startDate).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
      const formattedEndDate = new Date(dataPoint?.endDate || endDate).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
      return `BmsFaults_FaultResolution_${resolutionType}_${formattedStartDate}-${formattedEndDate}.csv`;
    },

    // Level 2: Custom filters
    getLevel2Filters: (level2Record, dataPoint, baseFilters) => {
      const resolutionType = dataPoint.metricName === "Auto Resolved" ? "AutoResolved" : "ManualResolved";
      const errorDescription = level2Record?.xLabel || level2Record?.faultName;
      return [
        ...baseFilters,
        { name: "ResolutionType", values: [resolutionType] },
        { name: "errorDescription", values: [errorDescription] },
      ];
    },

    // Level 2: Custom select and metaInfo
    getLevel2Select: () => ["ResolvedFaultCount"],
    getLevel2MetaInfo: () => resolveMetaInfo(["licensePlateNumber", "originCountry", "originState", "originCity", "originDepo", "pingStatus"], "autoResolvedVehiclesTable"),

    // Level 2: Custom CSV filename
    level2GetCSVFileName: (level2Record, dataPoint) => {
      const faultName = level2Record?.xLabel || level2Record?.faultName || "Fault";
      const resolutionType = dataPoint?.metricName || "Resolved";
      const formattedStartDate = new Date(dataPoint?.startDate || startDate).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
      const formattedEndDate = new Date(dataPoint?.endDate || endDate).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
      return `BmsFaults_${baseEntityTypePluralLable}_${faultName}_${resolutionType}_${formattedStartDate}-${formattedEndDate}.csv`;
    },
  }), [startDate, endDate, baseEntityTypePluralLable, resolveTable, resolveMetaInfo]);

  // Use the drill-down hook
  const drillDown = useChartDrillDown({
    startDate,
    endDate,
    hierarchicalLocation,
    fleetIds,
    alertCategories,
    operationalStatus,
    filters,
    config: drillDownConfig,
  });

  // Calculate domains for Y-axis with nice scale
  const calculateDomains = () => {
    let faultValues = [];

    if (chartData.categories.length > 0) {
      faultValues = [...chartData.autoResolvedData, ...chartData.manuallyResolvedData];
    }

    const maxFault = Math.max(...faultValues, 0);

    // Calculate nice scale for fault axis
    const faultScale = calculateNiceScale(0, maxFault, 6); // Target 5 intervals (6 ticks)

    return {
      faultScale
    };
  };

  // Handle chart data point click - chart-specific logic
  const handleDataPointClick = (event, chartContext, config) => {
    const dataPointIndex = config.dataPointIndex;
    const seriesIndex = config.seriesIndex;

    // Get the series name
    const seriesNames = ["Auto Resolved", "Manually Resolved"];
    const seriesName = seriesNames[seriesIndex];

    // Get the category (X-axis value) - formatted for display
    const category = chartData.categories[dataPointIndex];

    // Get the original date range from API response
    const originalDateRange = chartData.dateRanges[dataPointIndex];

    // Parse the date range to get actual startDate and endDate
    const { startDate: clickedStartDate, endDate: clickedEndDate } = parseDateRangeFromLabel(originalDateRange, startDate, endDate);

    // Get the value
    let value = 0;
    switch (seriesIndex) {
      case 0:
        value = chartData.autoResolvedData[dataPointIndex] || 0;
        break;
      case 1:
        value = chartData.manuallyResolvedData[dataPointIndex] || 0;
        break;
      default:
        value = 0;
    }

    // Prepare data object
    const clickedData = {
      category: category,
      metricName: seriesName,
      value: value,
      dataPointIndex: dataPointIndex,
      seriesIndex: seriesIndex,
      startDate: clickedStartDate,
      endDate: clickedEndDate
    };

    // Use hook's handler
    drillDown.handleDataPointClick(clickedData);
  };

  // Fetch chart data from API
  useEffect(() => {
    const fetchChartData = async () => {
      if (!startDate || !endDate) {
        setLoading(false);
        return;
      }

      // Create a string representation of all dependencies
      const currentDeps = JSON.stringify({
        startDate,
        endDate,
        granularity,
        filters,
        hierarchicalLocation,
        fleetIds,
        onboardingLocations,
        allOnboardingLocations,
        alertCategories,
        operationalStatus,
        searchDevices: searchTerm?.selectedDevices,
        searchColumn: searchTerm?.selectedSearchColumn
      });

      // Skip if dependencies haven't actually changed
      if (prevDepsRef.current === currentDeps) {
        return;
      }

      prevDepsRef.current = currentDeps;

      try {
        setLoading(true);

        // Calculate granularity based on date range if not provided
        let effectiveGranularity = granularity;
        if (!effectiveGranularity) {
          effectiveGranularity = calculateGranularity(startDate, endDate);
        }

        // Convert granularity to uppercase for API
        const apiGranularity = effectiveGranularity.toUpperCase();

        // Prepare search filters (only if searchTerm is provided and has devices)
        const searchFilters = (searchTerm?.selectedDevices?.length > 0)
          ? [{
            name: searchTerm.selectedSearchColumn || "searchID",
            values: searchTerm.selectedDevices
          }]
          : [];

        const apiFilters = {
          startDate: formatDateForAPI(startDate),
          endDate: formatDateForAPI(endDate),
          select: 'ResolvedFaultCount',
          groupBy: 'ResolutionType',
          granularity: apiGranularity,
          filters: [
            ...filters,
            ...searchFilters
          ],
        };

        // Prepare global filters with hierarchical location, customer scope, alert
        // categories, and operational status. An absent fleetIds sends no fleetIDs
        // header, which is how this chart has always behaved.
        const globalFilters = {
          hierarchicalLocation: hierarchicalLocation,
          fleets: fleetIds,
          // The depot half of the same scope; the originLocations header is
          // derived from these two, so omitting them ignores a depot selection.
          onboardingLocations: onboardingLocations,
          allOnboardingLocations: allOnboardingLocations,
          alertCategories: alertCategories,
          operationalStatus: operationalStatus
        };

        const response = await getDiagnosticsAnalyticsTimeSeries(globalFilters, apiFilters);
        const timeSeriesResponse = response?.response?.data || [];

        // Transform API response to chart format
        const categories = [];
        const dateRanges = [];
        const autoResolvedData = [];
        const manuallyResolvedData = [];

        // Process each time period from the response
        timeSeriesResponse.forEach(item => {
          categories.push(formatDateLabel(item.xLabel));
          // Store original xLabel (date range) for later use
          dateRanges.push(item.xLabel);

          let autoResolved = 0;
          let manuallyResolved = 0;

          // Extract values from metricsArray
          if (item.metricsArray && Array.isArray(item.metricsArray)) {
            item.metricsArray.forEach(metric => {
              if (metric.type === 'autoresolved') {
                autoResolved = metric.value || 0;
              } else if (metric.type === 'manualresolved') {
                manuallyResolved = metric.value || 0;
              }
            });
          }

          autoResolvedData.push(autoResolved);
          manuallyResolvedData.push(manuallyResolved);
        });

        setChartData({
          categories,
          dateRanges,
          autoResolvedData,
          manuallyResolvedData
        });
      } catch (error) {
        console.error("Error fetching auto-resolved chart data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchChartData();
  }, [startDate, endDate, granularity, filters, hierarchicalLocation, fleetIds, onboardingLocations, allOnboardingLocations, alertCategories, operationalStatus, searchTerm]);

  // Initial mount - force single rerender to fix chart width calculation
  useEffect(() => {
    const timer = setTimeout(() => {
      setMounted(true);
    }, 50);
    return () => clearTimeout(timer);
  }, []);

  // Force chart to resize when sidebar state changes
  useEffect(() => {
    if (!mounted) return;

    // Show loader during transition
    setIsResizing(true);

    // Trigger resize immediately when sidebar state changes
    window.dispatchEvent(new Event('resize'));

    // Trigger again after transition completes to ensure proper sizing
    const timer = setTimeout(() => {
      window.dispatchEvent(new Event('resize'));
      // Hide loader after transition completes
      setIsResizing(false);
    }, 350);

    return () => clearTimeout(timer);
  }, [isSidebarExpanded, mounted]);

  // Store raw data for labels and tooltips
  const rawAutoResolvedData = chartData.autoResolvedData;
  const rawManuallyResolvedData = chartData.manuallyResolvedData;

  // Calculate max value from all data points
  const allValues = [...rawAutoResolvedData, ...rawManuallyResolvedData];
  const maxValue = Math.max(...allValues, 0);

  // Set minimum bar height for clickability (3% of max value)
  const NO_VALUE_MIN_HEIGHT = maxValue > 0 ? maxValue * 0.00 : 1;  // ~2% of max for zero values
  const MIN_HEIGHT = maxValue > 0 ? maxValue * 0.08 : 1;          // ~3% of max as reasonable min height

  // Transform data: ensure visual bars are at least MIN_HEIGHT
  // For grouped bars, each series is independent
  const visualAutoResolvedData = rawAutoResolvedData.map((value) => {
    if (value === 0) return NO_VALUE_MIN_HEIGHT;
    return Math.max(value, MIN_HEIGHT);
  });

  const visualManuallyResolvedData = rawManuallyResolvedData.map((value) => {
    if (value === 0) return NO_VALUE_MIN_HEIGHT;
    return Math.max(value, MIN_HEIGHT);
  });

  // Calculate max value from visual data for yAxis
  const allVisualValues = [...visualAutoResolvedData, ...visualManuallyResolvedData];
  const visualMaxValue = Math.max(...allVisualValues, 0);

  // Recalculate fault scale with visual max value
  const visualFaultScale = calculateNiceScale(0, visualMaxValue, 6);

  // Chart dimensions for Y-axis label positioning
  const chartHeight = 315; // Height of the chart plot area (matching SeverityOfFaultsChartView)
  const topOffset = 45; // Padding from top for the chart grid
  const bottomOffset = 37; // Padding from bottom for x-axis labels

  const faultLabels = generateYLabelsWithPositions(visualFaultScale, chartHeight, topOffset, bottomOffset);

  // Calculate dynamic width based on number of data points
  const minWidth = chartData.categories.length * 80; // Minimum width per data point

  // Unique chart ID for custom legend integration
  const chartId = "auto-resolved-chart";

  // Prepare legend series data for RenderChartLegend component
  const legendSeries = [
    { name: "Auto Resolved", color: "#008A15" },
    { name: "Manually Resolved", color: "#B0CC57" },
  ];

  // Calculate how many categories to show at once
  const categoriesCount = chartData.categories.length;
  const maxVisibleCategories = Math.max(Math.floor(800 / 200), 3);

  const chartOptions = {
    chart: {
      id: chartId,
      type: "bar",
      height: chartHeight,
      toolbar: {
        show: false,
      },
      zoom: {
        enabled: categoriesCount > maxVisibleCategories,
        type: 'x',
        autoScaleYaxis: false,
      },
      animations: {
        enabled: true,
        dynamicAnimation: {
          enabled: true,
          speed: 300,
        },
        animateGradually: {
          enabled: true,
          delay: 0,
        },
        easing: 'easeinout',
      },
      events: {
        dataPointSelection: inDetailsView ? null : handleDataPointClick,
      },
    },
    plotOptions: {
      bar: {
        horizontal: false,
        columnWidth: "24px",
        borderRadius: 2,
      },
    },
    colors: ["#008A15", "#B0CC57"],
    xaxis: {
      categories: chartData.categories,
      labels: {
        style: {
          colors: "#494949",
          fontSize: "12px",
        },
      },
      axisBorder: {
        color: "#E0E0E0",
      },
      axisTicks: {
        color: "#E0E0E0",
      },
      ...(categoriesCount > maxVisibleCategories && {
        range: maxVisibleCategories,
      }),
    },
    yaxis: {
      min: visualFaultScale.min,
      max: visualFaultScale.max,
      tickAmount: 5,
      forceNiceScale: false,
      labels: {
        formatter: (val) => val.toFixed(0)
      },
      show: false, // Hide ApexCharts Y-axis, use custom instead
    },
    legend: {
      show: false,
    },
    grid: {
      show: true,
      borderColor: "#F0F0F0",
      strokeDashArray: 3,
      padding: {
        left: 0,
        right: 0,
        top: 15, // Explicit top padding to match axis offset
        bottom: 0, // Bottom padding for grid lines (not including x-axis)
      },
      xaxis: {
        lines: {
          show: false,
        }
      },
    },
    dataLabels: {
      enabled: false,
    },
    tooltip: {
      shared: true,
      intersect: false,
      followCursor: true,
      style: {
        fontSize: "12px",
      },
      y: {
        formatter: function (val, opts) {
          // Get actual values from raw data for tooltip
          const dataPointIndex = opts.dataPointIndex;
          const seriesIndex = opts.seriesIndex;

          let actualValue = val;
          if (seriesIndex === 0) {
            actualValue = rawAutoResolvedData[dataPointIndex] || 0;
          } else if (seriesIndex === 1) {
            actualValue = rawManuallyResolvedData[dataPointIndex] || 0;
          }

          return actualValue + " faults";
        },
      },
    },
    states: {
      active: {
        filter: {
          type: 'none',
        },
      },
    },
  };

  const series = [
    {
      name: "Auto Resolved",
      data: visualAutoResolvedData,
    },
    {
      name: "Manually Resolved",
      data: visualManuallyResolvedData,
    },
  ];

  // Calculate if drill-down is enabled
  const drillDownEnabled = !inDetailsView && drillDown.getTableConfig(1);

  if (!mounted || isResizing || loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: chartHeight + 50,
        }}
      >
        <CartLoader />
      </Box>
    );
  }

  return (
    <Box sx={{ mt: 3, width: "100%", overflow: "hidden" }}>
      {/* Legend */}
      <Box sx={{ mb: 2 }}>
        <RenderChartLegend
          legendSeries={legendSeries}
          chartIDs={[chartId]}
          isLoading={loading}
          centered={true}
        />
      </Box>

      {/* Chart Container - Scrollable with Fixed Y-Axis */}
      <ScrollableChartWrapper
        chartOptions={chartOptions}
        series={series}
        chartType="bar"
        chartHeight={chartHeight}
        leftAxis={{
          label: "Fault Count",
          labelData: faultLabels
        }}
        isSidebarExpanded={isSidebarExpanded}
        minWidth={minWidth}
        categoriesCount={chartData.categories.length}
        drillDownEnabled={drillDownEnabled}
      />
      {drillDown.getTableConfig(1) && (
        <ChartDrillDownModalV2
          open={drillDown.modalOpen}
          onClose={drillDown.handleModalClose}
          title={drillDown.getModalTitle}
          selectedDataPoint={drillDown.selectedDataPoint}
          tableConfigs={drillDown.getTableConfig(1)}
          getTableData={drillDown.getTableData}
          applyTableAction={drillDown.applyTableAction}
          getCSVFileName={drillDown.getCSVFileName}
          level2TableConfigs={drillDown.getTableConfig(2)}
          level2GetTableData={drillDown.level2GetTableData}
          level2ApplyTableAction={drillDown.level2ApplyTableAction}
          level2GetCSVFileName={drillDown.level2GetCSVFileName}
          level2Title={drillDown.level2Title}
        />
      )}
    </Box>
  );
};

export default AutoResolvedChartView;
